import React from 'react';
import * as Yup from 'yup';

import type { JSONSchema4 } from 'json-schema';

import type {
  FieldId,
  FormElementSchema,
  FormFieldDef,
  FormSchema,
} from '../types';
import { ElementType, FieldType } from './parser';
import { getRecordTotal } from '../components/input/ItemizedPercentileList/helpers';
import { ProductTypes, type ProductType } from '../../types';
import {
  PRIME_ONE_AU_NON_AUSTRALIA_REVENUE_FIELD_ID,
  PRIME_ONE_AU_NON_AUSTRALIA_EXCLUDING_USA_REVENUE_FIELD_ID,
  PRIME_ONE_AU_NON_AUSTRALIA_USA_REVENUE_FIELD_ID,
  PRIME_ONE_AU_REVENUE_PERCENTAGE_SPLIT_FIELD_ID,
} from '../constants';
import {
  areVisibilityConditionsMet,
  getDependentFieldIds,
} from '../utils/visibility';

type UseFormValidationSchemaProps = {
  formSchema?: FormSchema;
  productType?: ProductType;
  /** When true, required flags from form metadata and JSON schema do not block submit. */
  readonly relaxRequiredValidation?: boolean;
};

const parseCurrencyValue = (value: any): number => {
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string') {
    return value === '£' || value === '$' || value === ''
      ? 0
      : parseFloat(value.replace(/[£$,]/g, ''));
  }
  return 0;
};

export const useFormValidationSchema = (
  props: UseFormValidationSchemaProps
) => {
  const schema = React.useMemo(() => {
    // Fields nested under Sections inherit those section-level conditions so
    // unanswered required fields inside a hidden section are not validated.
    // Without this, validation would silently block submit on questions the
    // user can't see (e.g. "Past Incidents" hidden when "Had any past Cyber
    // Incidents?" is "No").
    const sectionConditionsByFieldId = buildSectionConditionsByFieldId(
      props.formSchema?.elements
    );

    const obj = Object.values(
      props.formSchema?.fields ?? {}
    ).reduce<RecordOfSchemas>((acc, formFieldDef) => {
      let fieldYupSchema = getYupSchemaForField(
        formFieldDef,
        props.relaxRequiredValidation
      );
      const fieldKey = formFieldDef.id;

      if (!fieldYupSchema) {
        return acc;
      }

      const sectionConditions = sectionConditionsByFieldId[fieldKey] ?? [];
      for (const conditions of sectionConditions) {
        fieldYupSchema = wrapSchemaWithVisibilityConditions(
          conditions,
          fieldYupSchema
        );
      }

      return { ...acc, [fieldKey]: fieldYupSchema };
    }, {});

    let baseSchema = Yup.object(obj);

    if (props.productType === ProductTypes.prime_one_au) {
      baseSchema = baseSchema.shape({
        [PRIME_ONE_AU_REVENUE_PERCENTAGE_SPLIT_FIELD_ID]:
          buildItemizedPercentileListYupSchema({ required: true }),
      });

      baseSchema = baseSchema.test(
        'non-australia-revenue-sum',
        'Annual Revenue Non-Australian excluding and including USA must sum to Annual Revenue Non-Australia',
        function (values) {
          const excludingUsaField =
            values?.[PRIME_ONE_AU_NON_AUSTRALIA_EXCLUDING_USA_REVENUE_FIELD_ID];
          const usaField =
            values?.[PRIME_ONE_AU_NON_AUSTRALIA_USA_REVENUE_FIELD_ID];
          const totalField =
            values?.[PRIME_ONE_AU_NON_AUSTRALIA_REVENUE_FIELD_ID];

          if (!excludingUsaField || !usaField || !totalField) {
            return true;
          }

          const excludingUsaValue = excludingUsaField?.value;
          const usaValue = usaField?.value;
          const totalValue = totalField?.value;

          if (totalValue === undefined || totalValue === null) {
            return true;
          }

          const excludingUsaRevenue =
            excludingUsaValue === undefined ||
            excludingUsaValue === null ||
            excludingUsaValue === ''
              ? 0
              : parseCurrencyValue(excludingUsaValue);
          const usaRevenue =
            usaValue === undefined || usaValue === null || usaValue === ''
              ? 0
              : parseCurrencyValue(usaValue);
          const totalRevenue =
            totalValue === '' ? 0 : parseCurrencyValue(totalValue);

          const calculatedSum = excludingUsaRevenue + usaRevenue;

          const isValid = Math.abs(calculatedSum - totalRevenue) < 0.01;

          if (!isValid) {
            return this.createError({
              path: PRIME_ONE_AU_NON_AUSTRALIA_REVENUE_FIELD_ID,
              message:
                'Annual Revenue Non-Australian excluding and including USA must sum to Annual Revenue Non-Australia',
            });
          }

          return true;
        }
      );
    }

    return baseSchema;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.formSchema?.id, props.productType, props.relaxRequiredValidation]);

  return schema;
};

export const getYupSchemaForField = (
  field: FormFieldDef,
  relaxRequiredValidation?: boolean
) => {
  let baseSchema = null;

  if (field.type == FieldType.ItemizedPercentileList) {
    baseSchema = buildItemizedPercentileListYupSchema();
  }

  if (field.type == FieldType.NaicsClassDescription) {
    baseSchema = buildNaicsClassDescriptionYupSchema(relaxRequiredValidation);
  }

  if (field.type == FieldType.JobList) {
    return null;
  }

  if (field.schema) {
    baseSchema = generateYupSchemaFromJSONSchema(
      field.schema,
      field.id,
      field.text,
      field.type,
      undefined,
      relaxRequiredValidation ? false : field.required,
      relaxRequiredValidation
    );
  }

  if (field.conditions && baseSchema) {
    return buildConditionalSchema(
      field.conditions,
      baseSchema as Yup.ObjectSchema<any>
    );
  }

  return baseSchema;
};

type SectionConditions = NonNullable<FormFieldDef['conditions']>;

function buildSectionConditionsByFieldId(
  elements: FormElementSchema[] | undefined,
  inherited: SectionConditions[] = [],
  acc: Record<FieldId, SectionConditions[]> = {}
): Record<FieldId, SectionConditions[]> {
  if (!Array.isArray(elements)) {
    return acc;
  }

  for (const element of elements) {
    if (element.type === ElementType.Section) {
      const nextInherited = element.conditions
        ? [...inherited, element.conditions]
        : inherited;
      buildSectionConditionsByFieldId(element.elements, nextInherited, acc);
    } else if (element.type === ElementType.Field) {
      acc[element.fieldId] = inherited;
    }
  }

  return acc;
}

// Wraps a field's Yup schema so it skips validation when the field is hidden,
// deciding visibility through the same utils/visibility helpers the UI uses.
// Preserves the field's underlying required/optional behavior when it IS
// visible rather than forcing required.
function wrapSchemaWithVisibilityConditions(
  visibilityConditions: SectionConditions,
  baseSchema: Yup.Schema<any>
): Yup.Schema<any> {
  const dependentFieldIds = getDependentFieldIds(visibilityConditions);

  return baseSchema.when(dependentFieldIds, {
    is: (...values: any[]) => {
      const valueByFieldId = Object.fromEntries(
        dependentFieldIds.map((fieldId, index) => [fieldId, values[index]])
      );
      return areVisibilityConditionsMet(visibilityConditions, valueByFieldId);
    },
    then: (schema: any) => schema,
    otherwise: () => Yup.mixed().notRequired(),
  });
}

const buildConditionalSchema = (
  conditions: FormFieldDef['conditions'],
  baseSchema: Yup.Schema<any>
): Yup.Schema<any> => {
  if (!conditions) {
    return baseSchema;
  }

  return wrapSchemaWithVisibilityConditions(conditions, baseSchema);
};

const generateYupSchemaFromJSONSchema = (
  jsonSchema: JSONSchema4,
  key: string,
  label: string,
  fieldType: FieldType,
  parentNodeJsonSchema?: JSONSchema4,
  requiredOverride?: boolean,
  relaxRequiredValidation?: boolean
): Yup.Schema<any> | null => {
  // Handle JSON Schema constructs that don't require a direct type
  if (
    jsonSchema.$ref ||
    jsonSchema.allOf ||
    jsonSchema.anyOf ||
    jsonSchema.oneOf
  ) {
    // These are valid JSON Schema constructs but we don't support them yet
    // Return null to skip validation for these fields gracefully
    return null;
  }

  let yupSchema = null;

  if (jsonSchema.type == 'object') {
    if (jsonSchema.properties) {
      yupSchema = Yup.object(
        Object.keys(jsonSchema.properties).reduce<RecordOfSchemas>(
          (acc, currKey) => {
            const childNodeYupSchema = generateYupSchemaFromJSONSchema(
              jsonSchema.properties![currKey],
              currKey,
              label,
              fieldType,
              jsonSchema,
              requiredOverride,
              relaxRequiredValidation
            );

            if (!childNodeYupSchema) {
              return acc;
            }

            return { ...acc, [currKey]: childNodeYupSchema };
          },
          {}
        )
      );
    }
  }

  if (fieldType === FieldType.FieldList) {
    yupSchema = buildFieldListValidation(jsonSchema, relaxRequiredValidation);
  }

  if (fieldType === FieldType.GroupedPercentileList) {
    yupSchema = buildGroupedPercentileListValidation(jsonSchema);
  }

  if (
    fieldType === FieldType.MultiSelect &&
    getIsFieldRequired(
      key,
      parentNodeJsonSchema,
      requiredOverride,
      relaxRequiredValidation
    )
  ) {
    yupSchema = Yup.mixed().test(
      'is-non-empty-array',
      'At least one selection is required',
      function (value) {
        if (!Array.isArray(value)) {
          return false;
        }

        const isOneValid = value.some((val: { value: any }) => {
          return typeof val.value === 'string' && val.value.trim() !== '';
        });

        return isOneValid;
      }
    );
  }

  if (jsonSchema.type == 'number') {
    switch (fieldType) {
      case FieldType.Currency:
        yupSchema = Yup.number().fromCurrency().label(label);
        break;
      default:
        yupSchema = Yup.number()
          .nullable()
          .transform((value, ogValue) => {
            return ogValue === '' ? null : value;
          })
          .label('This');
        break;
    }

    if (
      getIsFieldRequired(
        key,
        parentNodeJsonSchema,
        requiredOverride,
        relaxRequiredValidation
      )
    ) {
      yupSchema = yupSchema.required('This is a required field');
    }

    if (numberHasMinimumConstraint(key, parentNodeJsonSchema)) {
      const minValue = getNumberMinimumValue(key, parentNodeJsonSchema);
      yupSchema = yupSchema.min(minValue as number);
    }

    if (numberHasMaximumConstraint(key, parentNodeJsonSchema)) {
      const maxValue = parentNodeJsonSchema?.properties?.[key].maximum;
      if (maxValue) {
        yupSchema = yupSchema.max(maxValue as number);
      }
    }
  }

  if (jsonSchema.type === 'string') {
    yupSchema = getIsFieldRequired(
      key,
      parentNodeJsonSchema,
      requiredOverride,
      relaxRequiredValidation
    )
      ? Yup.string()
          .nullable()
          .label(label)
          .required('This is a required field')
      : Yup.string().nullable().label(label);

    // Apply minLength constraint
    if (typeof jsonSchema.minLength === 'number') {
      yupSchema = yupSchema.min(
        jsonSchema.minLength,
        `${label} must be at least ${jsonSchema.minLength} characters`
      );
    }

    // Apply maxLength constraint
    if (typeof jsonSchema.maxLength === 'number') {
      yupSchema = yupSchema.max(
        jsonSchema.maxLength,
        `${label} must be at most ${jsonSchema.maxLength} characters`
      );
    }

    // Apply pattern constraint (regex)
    if (typeof jsonSchema.pattern === 'string') {
      try {
        const regexPattern = new RegExp(jsonSchema.pattern);
        yupSchema = yupSchema.matches(
          regexPattern,
          jsonSchema.patternErrorMessage || `${label} format is invalid`
        );
      } catch (error) {
        console.warn(
          `Invalid regex pattern for field "${label}": ${jsonSchema.pattern}`,
          error
        );
      }
    }
  }

  // mutate various common config options
  if (yupSchema && Array.isArray(jsonSchema.enum)) {
    yupSchema.clone().oneOf(jsonSchema.enum as []);
  }

  return yupSchema;
};

const getIsFieldRequired = (
  fieldKey: string,
  parentNodeJsonSchema?: JSONSchema4,
  requiredOverride?: boolean,
  relaxRequiredValidation?: boolean
) => {
  if (relaxRequiredValidation) {
    return false;
  }

  // Element-level required forces only the field's answer key ('value'), matching how
  // schema-required fields declare `"required": ["value"]`. Sibling and nested-compound
  // properties (e.g. comment, props.*) keep their own schema-driven required semantics.
  if (requiredOverride === true && fieldKey === 'value') {
    return true;
  }

  return (
    parentNodeJsonSchema &&
    Array.isArray(parentNodeJsonSchema.required) &&
    parentNodeJsonSchema.required.includes(fieldKey)
  );
};

const numberHasMinimumConstraint = (
  fieldKey: string,
  parentNodeJsonSchema?: JSONSchema4
) => {
  return (
    parentNodeJsonSchema &&
    typeof parentNodeJsonSchema.properties?.[fieldKey]?.minimum == 'number'
  );
};

const numberHasMaximumConstraint = (
  fieldKey: string,
  parentNodeJsonSchema?: JSONSchema4
) => {
  return (
    parentNodeJsonSchema &&
    typeof parentNodeJsonSchema.properties?.[fieldKey]?.maximum == 'number'
  );
};

const getNumberMinimumValue = (
  fieldKey: string,
  parentNodeJsonSchema?: JSONSchema4
) => {
  return parentNodeJsonSchema?.properties?.[fieldKey].minimum;
};

const numberValueNodeSchema = Yup.object({
  comment: Yup.string().nullable(),
  value: Yup.number(),
});

export const stringValueNodeSchema = Yup.object({
  comment: Yup.string().nullable(),
  value: Yup.string(),
});

export const buildItemizedPercentileListYupSchema = (options?: {
  required?: boolean;
}) => {
  return Yup.object({
    props: Yup.lazy((value: Record<string, any>) => {
      const schema = Object.keys(value ?? {}).reduce<Record<string, any>>(
        (acc, key) => ({
          ...acc,
          [key]: stringValueNodeSchema,
        }),
        {}
      );

      return Yup.object(schema).test({
        name: 'Totals 100 or 0',
        message: 'Should total 100%',
        test: (val) => {
          const recordTotal = getRecordTotal({ props: val });
          return options?.required
            ? recordTotal == 100
            : recordTotal == 100 || recordTotal == 0;
        },
      });
    }),
  });
};

const buildNaicsClassDescriptionYupSchema = (
  relaxRequiredValidation?: boolean
) => {
  const propsSchema = Yup.object()
    .shape({
      code: stringValueNodeSchema,
    })
    .nullable();

  return Yup.object({
    props: relaxRequiredValidation
      ? propsSchema
      : propsSchema.required('This is a required field'),
  });
};

function buildFieldListValidation(
  jsonSchema: JSONSchema4,
  relaxRequiredValidation?: boolean
) {
  const items = jsonSchema?.properties?.values?.items;

  if (!(Array.isArray(items) && items[0]?.properties?.props?.properties)) {
    return null;
  }

  const propsSchema = items[0]?.properties?.props?.properties ?? {};

  const requiredFields = items[0]?.properties?.props?.required ?? [];

  if (relaxRequiredValidation) {
    return Yup.object({
      values: Yup.array().of(
        Yup.object({
          props: Yup.object(),
        })
      ),
    });
  }

  const propsYupShape: Record<string, Yup.Schema<any>> = {};

  for (const fieldKey of requiredFields as string[]) {
    const fieldDefinition = propsSchema[fieldKey];
    const valueSchemaDef = fieldDefinition?.properties?.value;
    const isNumber = valueSchemaDef?.type === 'number';

    propsYupShape[fieldKey] = Yup.object({
      value: Yup.mixed()
        .test(
          'field-validation',
          `${fieldDefinition.label} is invalid`,
          (val) => {
            const rawValue = val;

            if (
              rawValue == null ||
              (typeof rawValue === 'string' && rawValue.trim() === '')
            ) {
              return false;
            }

            if (isNumber) {
              const parsed = parseFloat(rawValue);
              const isValid = !Number.isNaN(parsed);
              return isValid;
            }

            return true;
          }
        )
        .required(`${fieldKey} is required`),
    });
  }

  const schema = Yup.object({
    values: Yup.array().of(
      Yup.object({
        props: Yup.object(propsYupShape),
      })
    ),
  });

  return schema;
}

function buildGroupedPercentileListValidation(jsonSchema: JSONSchema4) {
  const items = jsonSchema?.properties?.values?.items;

  if (!Array.isArray(items)) {
    return null;
  }

  const schema = Yup.object({
    values: Yup.array()
      .of(
        Yup.object({
          props: Yup.object().test({
            name: 'Dynamic keys validation',
            message: 'Each key must have a valid value',
            test: (props) => {
              if (typeof props !== 'object' || props === null) {
                return false;
              }

              return Object.values(props).every((field) => {
                const typedField = field as { value: number };
                return typedField && typeof typedField.value === 'number';
              });
            },
          }),
        })
      )
      .test({
        name: 'Totals 100 or 0',
        message: 'The total percentage must equal 100%',
        test: (values) => {
          if (!Array.isArray(values)) {
            return false;
          }

          const total = values.reduce((sum: number, item) => {
            const props = item.props || {};
            const innerTotal = (
              Object.values(props) as Array<{ value: string }>
            ).reduce((innerSum: number, field) => {
              const value = parseFloat(field.value);
              return innerSum + (Number.isNaN(value) ? 0 : value);
            }, 0);
            return sum + innerTotal;
          }, 0);

          return total === 100 || total === 0;
        },
      }),
  });

  return schema;
}

type RecordOfSchemas = Record<string, Yup.Schema<any>>;
